/**
 * HelpVideo — a small Loom-style player for the narrated walkthrough videos
 * produced by the help-video pipeline (tests-e2e/helpvideos).
 *
 * Wraps a native <video> and adds:
 *   - a branded "poster" state before first play: near-opaque black wash with a
 *     large primary play control and short explainer under it (Loom / Vimeo vibe).
 *     Native controls stay hidden until first play;
 *   - a caption bar rendered from the VTT track's cues (themed, toggleable);
 *   - playback-speed buttons (1x / 1.1x / 1.5x / 2x).
 *
 * Captions are on by default. The track is kept in "hidden" mode so the browser
 * parses cues (firing `cuechange`) without drawing its own overlay.
 */

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ClosedCaptionRounded from "@mui/icons-material/ClosedCaptionRounded";
import ClosedCaptionOffRounded from "@mui/icons-material/ClosedCaptionOffRounded";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import PlaybackSpeedToggle from "./PlaybackSpeedToggle";

const HelpVideo = ({
  src,
  captions,
  caption,
  poster,
  maxWidth = 760,
  aspectRatio = "16 / 10", // matches the 1280x800 recordings; reserves height so
  // the box doesn't reflow/distort while metadata loads
  sx,
}) => {
  const { t } = useTranslation("home");
  const videoRef = useRef(null);
  const [speed, setSpeed] = useState(1);
  const [showCaptions, setShowCaptions] = useState(true);
  const [cue, setCue] = useState("");
  const [started, setStarted] = useState(false); // true once the user has played it

  // Read cues from the subtitle track ourselves. We force the track to "hidden"
  // so the browser fires cuechange without painting its own caption overlay.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !captions) return undefined;

    const bindTrack = () => {
      const track = video.textTracks && video.textTracks[0];
      if (!track) return undefined;
      track.mode = "hidden";
      const onCueChange = () => {
        const active = track.activeCues && track.activeCues[0];
        setCue(active ? active.text.replace(/<[^>]+>/g, "") : "");
      };
      track.addEventListener("cuechange", onCueChange);
      onCueChange();
      return () => track.removeEventListener("cuechange", onCueChange);
    };

    // The track element may not be ready on first paint.
    let cleanup = bindTrack();
    if (!cleanup) {
      const onLoaded = () => {
        cleanup = bindTrack();
      };
      video.addEventListener("loadedmetadata", onLoaded);
      return () => {
        video.removeEventListener("loadedmetadata", onLoaded);
        if (cleanup) cleanup();
      };
    }
    return cleanup;
  }, [captions]);

  // Latch `started` on the first play: the poster overlay hides for good and the
  // native controls take over (no overlay on subsequent pauses).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const onPlay = () => setStarted(true);
    video.addEventListener("play", onPlay);
    return () => video.removeEventListener("play", onPlay);
  }, []);

  const handleSpeed = (value) => {
    setSpeed(value);
    if (videoRef.current) videoRef.current.playbackRate = value;
  };

  const play = () => videoRef.current && videoRef.current.play();

  // Overlay is only the initial poster: once the user clicks play it fades out
  // and stays hidden — no overlay on pause afterwards.
  const showOverlay = !started;
  const isPoster = !started;

  return (
    <Box sx={{ maxWidth, mx: "auto", ...sx }}>
      <Box sx={{ position: "relative" }}>
        <Box
          component="video"
          ref={videoRef}
          controls={started}
          controlsList="nodownload"
          poster={poster}
          preload="metadata"
          sx={{
            width: "100%",
            aspectRatio,
            objectFit: "cover",
            display: "block",
            borderRadius: 2,
            boxShadow: 3,
            bgcolor: "common.black",
          }}
        >
          <source src={src} type="video/mp4" />
          {captions && (
            <track kind="subtitles" src={captions} srcLang="en" label="English" default />
          )}
        </Box>

        {/* Poster overlay: near-opaque wash + play disc + caption, shown only
            before the first play. It fades out on click and stays hidden — once
            started, the native controls own play/pause. */}
        <Box
          onClick={play}
          role="button"
          aria-label={t("helpVideo.play")}
          sx={{
            position: "absolute",
            inset: 0,
            bottom: started ? 44 : 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2.5,
            px: 4,
            cursor: "pointer",
            borderRadius: 2,
            opacity: showOverlay ? 1 : 0,
            pointerEvents: showOverlay ? "auto" : "none",
            bgcolor: (theme) =>
              alpha(theme.palette.common.black, isPoster ? 0.88 : 0),
            transition: (theme) =>
              theme.transitions.create(["opacity", "background-color"], {
                duration: theme.transitions.duration.shorter,
              }),
            "&:hover": {
              bgcolor: (theme) =>
                alpha(theme.palette.common.black, isPoster ? 0.92 : 0.28),
              "& .hv-play": { boxShadow: 8 },
            },
          }}
        >
          {/* Primary disc + white triangle — Loom/Vimeo-style CTA */}
          <Box
            className="hv-play"
            aria-hidden
            sx={{
              width: isPoster ? 80 : 64,
              height: isPoster ? 80 : 64,
              borderRadius: "50%",
              bgcolor: "primary.main",
              // contrastText, not white: dark mode's primary is a light blue
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: 6,
              transition: (theme) =>
                theme.transitions.create("box-shadow", {
                  duration: theme.transitions.duration.shorter,
                }),
            }}
          >
            <PlayArrowRounded sx={{ fontSize: isPoster ? 48 : 40, ml: "2px" }} />
          </Box>

          {isPoster && caption && (
            <Typography
              variant="body1"
              align="center"
              sx={{
                color: "common.white",
                opacity: 0.92,
                maxWidth: 400,
                lineHeight: 1.45,
                letterSpacing: 0.15,
              }}
            >
              {caption}
            </Typography>
          )}
        </Box>

        {showCaptions && cue && (
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              bottom: 56, // clear the native control bar
              maxWidth: "90%",
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              bgcolor: "rgba(0, 0, 0, 0.72)",
              pointerEvents: "none",
              textAlign: "center",
            }}
          >
            <Box
              component="span"
              sx={{ color: "common.white", typography: "body2", whiteSpace: "pre-line" }}
            >
              {cue}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          mt: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        {captions ? (
          <Tooltip title={showCaptions ? t("helpVideo.hideCaptions") : t("helpVideo.showCaptions")}>
            <IconButton
              size="small"
              onClick={() => setShowCaptions((v) => !v)}
              color={showCaptions ? "primary" : "default"}
              aria-label={t("helpVideo.toggleCaptions")}
            >
              {showCaptions ? <ClosedCaptionRounded /> : <ClosedCaptionOffRounded />}
            </IconButton>
          </Tooltip>
        ) : (
          <Box />
        )}

        <PlaybackSpeedToggle value={speed} onChange={handleSpeed} />
      </Box>
    </Box>
  );
};

HelpVideo.propTypes = {
  src: PropTypes.string.isRequired,
  captions: PropTypes.string,
  caption: PropTypes.string, // short explainer shown on the poster, under the play button
  poster: PropTypes.string, // still image shown before first play
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  aspectRatio: PropTypes.string, // CSS aspect-ratio, e.g. "16 / 10"
  sx: PropTypes.object,
};

export default HelpVideo;
